import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Search,
  X,
  Copy,
  Check,
  GitBranch,
  Users,
  Megaphone,
  CalendarX,
  FileCode2,
  Filter,
  Zap,
  Settings,
  Loader2,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { useVariablesInfinite } from "../../hooks/useVariablesInfinite";
import { Variable } from "../../types/variable";
import { useNavigate } from "react-router";

interface Command {
  id: string;
  name: string;
  description: string;
  icon: React.ComponentType<any>;
  action: () => void;
}

interface CommandSearchProps {
  isOpen: boolean;
  onClose: () => void;
}

const CommandSearch: React.FC<CommandSearchProps> = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [copiedVariable, setCopiedVariable] = useState<string | null>(null);
  const [showVariablesModal, setShowVariablesModal] = useState(false);
  const [variablesSearch, setVariablesSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);
  const variablesListRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Use infinite query for variables with search
  const {
    data: variablesData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading: variablesLoading,
    error: variablesError,
  } = useVariablesInfinite({ search: variablesSearch });

  // Flatten all variables from all pages
  const variables =
    variablesData?.pages.flatMap((page) => page.variables) || [];

  // Available commands
  const commands: Command[] = [
    {
      id: "variables",
      name: "Variables",
      description: "Browse and copy variables",
      icon: Settings,
      action: () => setShowVariablesModal(true),
    },
    {
      id: "flows",
      name: "Go to Flows",
      description: "Navigate to flows page",
      icon: GitBranch,
      action: () => {
        navigate("/flows");
        onClose();
      },
    },
    {
      id: "clients",
      name: "Go to Clients",
      description: "Navigate to clients page",
      icon: Users,
      action: () => {
        navigate("/clients");
        onClose();
      },
    },
    {
      id: "campaigns",
      name: "Go to Campaigns",
      description: "Navigate to campaigns page",
      icon: Megaphone,
      action: () => {
        navigate("/campaigns");
        onClose();
      },
    },
    {
      id: "quiet-periods",
      name: "Go to Quiet Periods",
      description: "Navigate to quiet periods page",
      icon: CalendarX,
      action: () => {
        navigate("/quiet-periods");
        onClose();
      },
    },
    {
      id: "templates",
      name: "Go to Templates",
      description: "Navigate to templates page",
      icon: FileCode2,
      action: () => {
        navigate("/templates");
        onClose();
      },
    },
    {
      id: "condition-rules",
      name: "Go to Condition Rules",
      description: "Navigate to condition rules page",
      icon: Filter,
      action: () => {
        navigate("/condition-rules");
        onClose();
      },
    },
    {
      id: "actions",
      name: "Go to Actions",
      description: "Navigate to actions page",
      icon: Zap,
      action: () => {
        navigate("/actions");
        onClose();
      },
    },
  ];

  // Filter commands based on query
  const filteredCommands = commands.filter(
    (command) =>
      command.name.toLowerCase().includes(query.toLowerCase()) ||
      command.description.toLowerCase().includes(query.toLowerCase()),
  );

  // Handle keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev < filteredCommands.length - 1 ? prev + 1 : 0,
      );
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelectedIndex((prev) =>
        prev > 0 ? prev - 1 : filteredCommands.length - 1,
      );
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (filteredCommands[selectedIndex]) {
        filteredCommands[selectedIndex].action();
      }
    } else if (e.key === "Escape") {
      onClose();
    }
  };

  // Reset selected index when commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands.length]);

  // Handle variable copy
  const handleVariableCopy = async (variable: Variable) => {
    try {
      await navigator.clipboard.writeText(variable.var_name);
      setCopiedVariable(variable.var_name);
      toast.success(`Variable "${variable.var_name}" copied to clipboard`);
      setTimeout(() => setCopiedVariable(null), 2000);
    } catch (err) {
      console.error("Failed to copy variable:", err);
      toast.error("Failed to copy variable to clipboard");
    }
  };

  // Handle modal close on outside click
  const handleModalClick = (e: React.MouseEvent) => {
    if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
      setShowVariablesModal(false);
    }
  };

  // Focus input when modal opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setQuery("");
      setSelectedIndex(0);
      setShowVariablesModal(false);
      setVariablesSearch("");
    }
  }, [isOpen]);

  // Debounce variables search
  useEffect(() => {
    const timer = setTimeout(() => {
      // The search will be handled by the useVariablesInfinite hook
    }, 300);
    return () => clearTimeout(timer);
  }, [variablesSearch]);

  // Infinite scroll handler
  const handleScroll = useCallback(() => {
    if (!variablesListRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = variablesListRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - 100;

    if (isNearBottom && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage]);

  // Attach scroll listener
  useEffect(() => {
    const listElement = variablesListRef.current;
    if (listElement && showVariablesModal) {
      listElement.addEventListener("scroll", handleScroll);
      return () => {
        listElement.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll, showVariablesModal]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-start justify-center pt-20"
        onClick={onClose}
      >
        {/* Command Search Modal */}
        <div
          className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center px-4 py-3 border-b border-gray-200">
            <Search className="h-5 w-5 text-gray-400 mr-3" />
            <input
              ref={inputRef}
              type="text"
              placeholder="Search commands... (↑↓ to navigate, Enter to select)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              className="flex-1 text-lg outline-none"
            />
            <button
              onClick={onClose}
              className="ml-3 p-1 hover:bg-gray-100 rounded"
            >
              <X className="h-5 w-5 text-gray-400" />
            </button>
          </div>

          {/* Commands List */}
          <div className="max-h-96 overflow-y-auto">
            {filteredCommands.length > 0 ? (
              filteredCommands.map((command, index) => {
                const Icon = command.icon;
                return (
                  <button
                    key={command.id}
                    onClick={command.action}
                    className={`w-full flex items-center px-4 py-3 text-left hover:bg-gray-50 transition-colors ${
                      index === selectedIndex
                        ? "bg-blue-50 border-r-2 border-blue-500"
                        : ""
                    }`}
                  >
                    <Icon className="h-5 w-5 text-gray-400 mr-3" />
                    <div className="flex-1">
                      <div className="font-medium text-gray-900">
                        {command.name}
                      </div>
                      <div className="text-sm text-gray-500">
                        {command.description}
                      </div>
                    </div>
                    {index === selectedIndex && (
                      <div className="text-xs text-blue-600 font-medium">
                        Press Enter
                      </div>
                    )}
                  </button>
                );
              })
            ) : (
              <div className="px-4 py-8 text-center text-gray-500">
                No commands found
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Variables Modal */}
      {showVariablesModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-[70] flex items-center justify-center p-4"
          onClick={handleModalClick}
        >
          <div
            ref={modalRef}
            className="bg-white rounded-lg shadow-xl w-full max-w-4xl max-h-[80vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Variables Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Variables
                </h2>
                {variablesData && (
                  <p className="text-sm text-gray-500 mt-1">
                    {variables.length} variable
                    {variables.length !== 1 ? "s" : ""} loaded
                  </p>
                )}
              </div>
              <button
                onClick={() => setShowVariablesModal(false)}
                className="p-1 hover:bg-gray-100 rounded"
              >
                <X className="h-5 w-5 text-gray-400" />
              </button>
            </div>

            {/* Search Input */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search variables by name..."
                  value={variablesSearch}
                  onChange={(e) => setVariablesSearch(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none"
                />
              </div>
            </div>

            {/* Variables List */}
            <div ref={variablesListRef} className="flex-1 overflow-y-auto p-6">
              {variablesLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
                  <span className="ml-2 text-gray-500">
                    Loading variables...
                  </span>
                </div>
              ) : variablesError ? (
                <div className="text-center text-red-500 py-8">
                  Error loading variables:{" "}
                  {variablesError instanceof Error
                    ? variablesError.message
                    : "Unknown error"}
                </div>
              ) : variables.length > 0 ? (
                <div className="space-y-3">
                  {variables.map((variable) => (
                    <div
                      key={variable.id}
                      className="flex items-center justify-between p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">
                          {variable.var_name}
                        </div>
                        {variable.description && (
                          <div className="text-sm text-gray-500 mt-1">
                            {variable.description}
                          </div>
                        )}
                        {variable.db_name && (
                          <div className="text-xs text-blue-600 mt-1">
                            Database: {variable.db_name}
                          </div>
                        )}
                        {variable.sql_query && (
                          <div className="text-xs text-gray-600 mt-2 p-2 bg-gray-50 rounded border-l-2 border-gray-300">
                            <div className="font-medium text-gray-700 mb-1">
                              SQL Query:
                            </div>
                            <code className="text-xs text-gray-600 break-all">
                              {variable.sql_query}
                            </code>
                          </div>
                        )}
                      </div>
                      <button
                        onClick={() => handleVariableCopy(variable)}
                        className="ml-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
                        title="Copy variable name"
                      >
                        {copiedVariable === variable.var_name ? (
                          <Check className="h-4 w-4 text-green-500" />
                        ) : (
                          <Copy className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  ))}

                  {/* Loading indicator for infinite scroll */}
                  {isFetchingNextPage && (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
                      <span className="ml-2 text-sm text-gray-500">
                        Loading more...
                      </span>
                    </div>
                  )}

                  {/* End of list indicator */}
                  {!hasNextPage && variables.length > 0 && (
                    <div className="text-center text-gray-400 py-4 text-sm">
                      No more variables to load
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  {variablesSearch
                    ? "No variables found matching your search"
                    : "No variables found"}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CommandSearch;
